/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, flow, ObservableMap, toJS, runInAction } from 'mobx';
import { generateDetail } from './actionCreator';
import fetch from '../fetch';
import FormData from 'form-data';
import forEach from 'lodash/forEach';
import ErrorsStore from './errorsStore';
import { generateList, generateListRefresh, generateListMore, generateUpdate, generateDelete } from './actionCreator';
import tasksStore from './tasksStore';
import MultipartUpload from './multipartUpload';
import _ from '../translations';

class VideosStore {
    @observable collection = new ObservableMap();
    @observable details = new ObservableMap();
    @observable progress = -1;
    @observable subtitleProgress = -1;
    @observable subtitleUploadConfig = {};

    constructor() {
        this.baseUrl = '/api/assets/videos';
    }

    @action
    reset = () => {
        this.collection.clear();
        this.details.clear();
        this.progress = -1;
    };

    @action.bound
    upload = flow(function*({ file = false, onProgress, abortCallback = false } = {}) {
        if (!file) return;
        this.progress = 0;
        let response = {};
        try {
            const uploader = new MultipartUpload(file);
            abortCallback(uploader.abort);
            response = yield uploader.start({
                onProgressChanged: (uploadedSize, totalSize, bitrate) => {
                    const percent = parseInt(100 * (uploadedSize / totalSize), 10);
                    runInAction(() => {
                        this.progress = percent;
                    });
                    if (onProgress) onProgress(percent);
                },
            });
            this.progress = -1;
            return response;
        } catch (e) {
            this.progress = -1;
            ErrorsStore.addError(e, _('There was a problem uploading video'));
            return;
        }
    });

    @action.bound
    uploadSubtitles = flow(function*({ file = false, onProgress, abortCallback = false } = {}) {
        if (!file) return;
        if (!this.subtitleUploadConfig.meta) yield this.getBucketConfiguration();
        const form = this.buildSubtitleForm(file);
        this.subtitleProgress = 0;
        let response = {};
        try {
            response = yield fetch.submit({
                form,
                url: this.subtitleUploadConfig.bucket,
                onProgress: percent => {
                    runInAction(() => {
                        this.subtitleProgress = percent;
                    });
                    if (onProgress) onProgress(percent);
                },
                abortCallback,
            });
        } catch (e) {
            this.subtitleProgress = -1;
            ErrorsStore.addError(e, _('There was a problem uploading subtitle'));
            return;
        }
        const path = this.generateSubtitleKey(file);
        if (response.statusCode === 204) {
            return this.subtitleUploadConfig.bucket + path;
        } else {
            ErrorsStore.addError({
                error: {
                    userMessage: _('Failed to upload subtitle'),
                    statusCode: 500,
                },
            });
        }
        this.subtitleProgress = -1;
    });

    @action.bound
    getBucketConfiguration = flow(function*() {
        try {
            const result = yield fetch.get({
                url: '/api/assets/s3/upload/subtitles',
                params: {},
            });
            if (result.error) {
                ErrorsStore.addError(result, _('Could not get S3 credentials'));
            }
            this.subtitleUploadConfig = result;
        } catch (e) {
            ErrorsStore.addError(e, _('Could not get S3 credentials'));
            console.warn(e);
        }
    });

    buildSubtitleForm = file => {
        const config = toJS(this.subtitleUploadConfig);
        const form = new FormData();
        forEach(config.meta, (field, key) => {
            if (key === 'key') form.append(key, this.generateSubtitleKey(file));
            else form.append(key, field);
        });
        form.append('file', file);
        return form;
    };

    // eslint-disable-next-line
    generateSubtitleKey = file => this.subtitleUploadConfig.meta.key.replace('${filename}', file.name);

    @action.bound
    detail = flow(function*({ _id = false } = {}) {
        return yield generateDetail({
            _id,
            url: `${this.baseUrl}/view/${_id}`,
            store: this,
            params: {
                json: true,
            },
            customError: _('There was a problem fetching the video'),
        })();
    });

    @action.bound
    list = flow(function*({ hash, params } = {}) {
        return yield generateList({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing videos'),
        })();
    });

    @action.bound
    listRefresh = flow(function*({ hash, params = {}, maxPagesToRefresh } = {}) {
        return yield generateListRefresh({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing videos'),
            maxPagesToRefresh,
        })();
    });

    @action.bound
    listMore = flow(function*({ hash, params = {} } = {}) {
        return yield generateListMore({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing videos'),
        })();
    });

    @action.bound
    create = flow(function*({ item = {} } = {}) {
        try {
            const detail = yield fetch.post({
                url: this.baseUrl,
                body: { data: item },
            });
            if (detail.error) {
                ErrorsStore.addError(detail, _('There was a problem creating the video'));
            } else {
                tasksStore.markAsDone(7);
            }
            return detail;
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem creating the video'));
            return false;
        }
    });

    @action.bound
    update = flow(function*({ items, item = {} } = {}) {
        return yield generateUpdate({
            items,
            item,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem updating video'),
        })();
    });

    @action.bound
    delete = flow(function*({ items, _id = false, type = 'logical' } = {}) {
        return yield generateDelete({
            items,
            _id,
            type,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem deleting the video'),
        })();
    });
}

export default new VideosStore();
